/** @jsxImportSource @compiled/react */
/* eslint-disable react/no-unknown-property */
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { cssMap } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import TallButton from './TallButton';

import googleLogo from '../images/google-logo.svg';
import microsoftLogo from '../images/microsoft-logo.svg';
import appleLogo from '../images/apple-logo.svg';
import slackLogo from '../images/slack-logo.svg';

import { SocialProvider } from '../types';
import { Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Image from '@atlaskit/image';

export type SocialLoginFormProps = {
  email?: string;
  highlightedSocialProvider: SocialProvider;
  handleGoogleLogin: () => void;
  handleMicrosoftLogin: () => void;
  handleAppleLogin: () => void;
  handleSlackLogin: () => void;
};

const styles = cssMap({
  socialLoginBlock: {
    padding: token('space.150'),
    borderStyle: 'solid',
    borderWidth: token('border.width'),
    borderColor: token('color.border'),
    borderRadius: token('radius.small'),
  },
  logo: {
    height: '24px',
    paddingRight: token('space.100'),
  },
});

const getLogo = (highlightedSocialProvider: SocialProvider) => {
  switch (highlightedSocialProvider) {
    case SocialProvider.Google:
      return googleLogo;
    case SocialProvider.Microsoft:
      return microsoftLogo;
    case SocialProvider.Apple:
      return appleLogo;
    case SocialProvider.Slack:
      return slackLogo;
  }
};

const getSocialLoginHandler = ({
  highlightedSocialProvider,
  handleGoogleLogin,
  handleMicrosoftLogin,
  handleAppleLogin,
  handleSlackLogin,
}: SocialLoginFormProps) => {
  switch (highlightedSocialProvider) {
    case SocialProvider.Google:
      return handleGoogleLogin;
    case SocialProvider.Microsoft:
      return handleMicrosoftLogin;
    case SocialProvider.Apple:
      return handleAppleLogin;
    case SocialProvider.Slack:
      return handleSlackLogin;
  }
};

const SocialLoginForm = ({
  email,
  highlightedSocialProvider,
  handleGoogleLogin,
  handleMicrosoftLogin,
  handleAppleLogin,
  handleSlackLogin,
}: SocialLoginFormProps) => {
  const handleSocialLogin = getSocialLoginHandler({
    highlightedSocialProvider,
    handleGoogleLogin,
    handleMicrosoftLogin,
    handleAppleLogin,
    handleSlackLogin,
  });

  return (
    <Stack space="space.100" xcss={styles.socialLoginBlock} testId="social-login-form">
      <Inline alignInline="center" alignBlock="center">
        <Image css={styles.logo} src={getLogo(highlightedSocialProvider)} alt="" />
        {email && <Text weight="semibold">{email}</Text>}
      </Inline>
      <TallButton
        isFullWidth
        id="social-login-submit"
        testId="social-login-submit-idf-testid"
        appearance="primary"
        onClick={handleSocialLogin}>
        <FormattedMessage
          id="social.login.form.continue-button"
          defaultMessage="Continue with {socialProviderName}"
          description="Button label that is a CTA for users to continue logging in using the selected social provider."
          values={{
            socialProviderName: highlightedSocialProvider.replace(/^./, str => str.toUpperCase()),
          }}
        />
      </TallButton>
    </Stack>
  );
};

export default SocialLoginForm;
